"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { FiInfo, FiActivity, FiClock, FiUser, FiMapPin, FiCalendar, FiPhone, FiFileText, FiCheckCircle, FiAlertCircle, FiUpload, FiSend, FiX, FiCheck, FiRefreshCw, FiTarget, FiShield, FiUserCheck, FiClipboard, FiArrowRight, FiXCircle, FiChevronRight, FiHash, FiUserPlus, FiPause, FiPlay, FiMail, FiFolder, FiTrash, FiEdit, FiPlus, FiDownload } from "react-icons/fi"
import { useParams } from "react-router-dom"
import axiosInstance from "../../../libs/axios"
import { uploadFilesWithPresignedPayload } from "../../../libs/presigned-upload"
import { useAuth } from "../../../provider/auth.provider"
import { useConfirm } from "../../../provider/confirm.provider"
import { StepDetailsOffcanvas } from "./stepdetails"
import { SelectWithSearch2 } from "../../../utils/select-with-search"
import toast from "react-hot-toast"
import { FaUser } from "react-icons/fa"
import ClaimTab from "./claim"
import CancelHoldModal from "./cancel_hold.modal"

const SITE_ENGINEER_ASSIGNABLE_ROLE_IDS = [7, 4]

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

type DetailRowProps = {
  label: string;
  value: string | number | boolean | null;
  icon?: React.ElementType;
}

function DetailRow({ label, value, icon: Icon }: DetailRowProps) {
  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-3">
        {Icon && <Icon className="text-blue-800 mt-1 flex-shrink-0" size={16} />}
        <div className="flex justify-between w-full items-center">
          <div className="text-sm font-medium text-gray-600 mb-1">{label}</div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-900 break-words">
              {value !== null && value !== undefined ? String(value).replace(/_/g, ' ') : 'N/A'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Types
interface User {
  id: number
  username: string
  first_name: string
  last_name: string
  email: string
}

interface UserObject {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface SiteData {
  id: number;
  site_id: string;
  project_id: number;
  activity: {
    id?: number;
    activity_name?: string;
  };
  reference_number: string;
  client_allocation_date: string;
  site_name: string;
  site_address: string;
  opco_name: string;
  opco_id: string;
  state_circle_id: number;
  district_id: number;
  pincode: number;
  site_latitude: string;
  site_longitude: string;
  site_priority: string;
  pr_number: string;
  pr_date: string;
  po_number: string;
  po_date: string;
  is_po_no_shared_at_allocation: boolean;
  technician_name: string;
  technician_number: string;
  fse_name: string;
  fse_number: string;
  aom_name: string;
  aom_number: string;
  client_project_name: string | null;
  customer_requirement: string;
  site_information1: string;
  site_information2: string;
  site_information3: string | null;
  site_engineer_assign_date: string | null;
  site_engineer_assign_time: string | null;
  site_engineer_username: string | null;
  site_engineer_userid: number;
  site_engineer_work_date: string;
  site_unlock_time: string;
  site_unlock_latitude: string;
  site_unlock_longitude: string;
  site_engineer_work_status: string;
  site_engineer_work_remarks: string;
  site_engineer_visit_status: string;
  ta_username: string;
  ta_userid: number;
  ta_work_date: string;
  ta_remarks: string;
  ta_se_quality_feedback: string;
  mis_username: string;
  mis_userid: number;
  mis_submission_date: string;
  mis_remarks: string | null;
  mis_validator_qa_feedback: string | null;
  report_designer_username: string;
  report_designer_userid: number;
  report_designer_work_date: string;
  report_designer_remarks: string;
  validator_username: string;
  validator_userid: number;
  validator_work_date: string;
  validator_remarks: string;
  validator_report_designer_qa_feedback: string;
  contact_person: string;
  contact_number: string;
  status: string;
  survey_workflow_status: string;
  cancel_reason: string | null;
  hold_reason: string | null;
  visit_remarks: string;
  created_by: number;
  is_location_verified: boolean;
  location_verification_attempts: number;
  last_location_check: string;
  created_at: string;
  updated_at: string;
  assigned_engineer_id: number | null;
  assigned_ta_id: number;
  assigned_report_maker_id: number | null;
  assigned_validator_id: number | null;
  site_engineer: UserObject;
  ta: UserObject;
  mis: UserObject;
  creator: UserObject;
  report_designer: UserObject;
  validator: UserObject;
  state_circle: {
    id: number;
    state_name: string;
    circle_name: string;
  };
  district: {
    id: number;
    name: string;
  };
  team_coordinator: UserObject;
  project: {
    project_name: string
  }
}

interface WorkflowLog {
  id: number
  site_id: number
  project_id: number
  action_type: string
  from_status?: string
  to_status?: string
  performed_by: number
  performed_at: string
  remarks?: string
  metadata?: any
  ip_address?: string
  user_agent?: string
  location_latitude?: string
  location_longitude?: string
  is_location_verified?: boolean
  created_at: string
  updated_at: string
  performer: User
}

interface SiteFile {
  id: number
  site_id: number
  file_name: string
  file_path: string
  file_size?: number
  file_type: string
  upload_stage: string
  uploaded_by: number
  uploaded_at: string
  uploader?: User
}

interface WorkflowUploadFile {
  file: File
  photoCategory?: string | null
}

interface UploadState {
  active: boolean
  percentage: number
  message: string
}

// Status badge component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusConfig = (status: string) => {
    const statusMap: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      site_created: { color: "bg-gray-100 text-gray-700", icon: <FiFileText size={12} />, label: "Site Created" },
      engineer_assigned: {
        color: "bg-blue-100 text-blue-900",
        icon: <FiUserCheck size={12} />,
        label: "Engineer Assigned",
      },
      field_work_in_progress: {
        color: "bg-yellow-100 text-yellow-700",
        icon: <FiClock size={12} />,
        label: "Field Work In Progress",
      },
      field_work_completed: {
        color: "bg-green-100 text-green-700",
        icon: <FiCheckCircle size={12} />,
        label: "Field Work Completed",
      },
      data_uploaded: { color: "bg-purple-100 text-purple-700", icon: <FiUpload size={12} />, label: "Data Uploaded" },
      ta_review_pending: {
        color: "bg-yellow-100 text-yellow-700",
        icon: <FiClock size={12} />,
        label: "Technical Associate Review Pending",
      },
      ta_review_completed: {
        color: "bg-teal-100 text-teal-700",
        icon: <FiCheck size={12} />,
        label: "Technical Associate Review Completed",
      },
      ta_rejected: { color: "bg-red-100 text-red-700", icon: <FiXCircle size={12} />, label: "Technical Associate Rejected" },
      mis_report_assign_pending: {
        color: "bg-yellow-100 text-yellow-700",
        icon: <FiClock size={12} />,
        label: "Mis Report Assign Pending",
      },
      report_maker_assigned: {
        color: "bg-indigo-100 text-indigo-700",
        icon: <FiClipboard size={12} />,
        label: "Report Maker Assigned",
      },
      report_in_progress: {
        color: "bg-yellow-100 text-yellow-700",
        icon: <FiClock size={12} />,
        label: "Report In Progress",
      },
      report_completed: {
        color: "bg-green-100 text-green-700",
        icon: <FiFileText size={12} />,
        label: "Report Completed",
      },
      report_maker_rejected: {
        color: "bg-red-100 text-red-700",
        icon: <FiXCircle size={12} />,
        label: "Report Maker Rejected",
      },
      validator_assigned: {
        color: "bg-blue-100 text-blue-900",
        icon: <FiShield size={12} />,
        label: "Validator Assigned",
      },
      validation_in_progress: {
        color: "bg-yellow-100 text-yellow-700",
        icon: <FiClock size={12} />,
        label: "Validation In Progress",
      },
      validation_completed: {
        color: "bg-green-100 text-green-700",
        icon: <FiCheckCircle size={12} />,
        label: "Validation Completed",
      },
      validation_rejected: {
        color: "bg-red-100 text-red-700",
        icon: <FiXCircle size={12} />,
        label: "Validation Rejected",
      },
      report_finalized: {
        color: "bg-green-100 text-green-700",
        icon: <FiCheckCircle size={12} />,
        label: "Report Finalized",
      },
      report_sent_to_client: {
        color: "bg-green-100 text-green-700",
        icon: <FiSend size={12} />,
        label: "Sent to Client",
      },
      cancelled: {
        color: "bg-red-100 text-red-700",
        icon: <FiSend size={12} />,
        label: "Cancelled",
      },
      hold: {
        color: "bg-yellow-100 text-yellow-700",
        icon: <FiSend size={12} />,
        label: "On Hold",
      }
    }
    return statusMap[status] || { color: "bg-gray-100 text-gray-700", icon: <FiInfo size={12} />, label: status }
  }

  const config = getStatusConfig(status)
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.icon}
      {config.label}
    </span>
  )
}

const ActionModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  title: string
  onSubmit: (data: any) => void
  uploadState?: UploadState
  fields: Array<{
    name: string
    label: string
    type: string
    required?: boolean
    accept?: string
    enablePhotoCategory?: boolean
    options?: Array<{ value: string; label: string }>
    shouldShow?: (formData: any) => boolean
  }>
}> = ({ isOpen, onClose, title, onSubmit, uploadState, fields }) => {
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [files, setFiles] = useState<Array<{
    file: File
    originalName: string
    newName: string
    nameWithoutExt: string
    extension: string
    preview?: string
    photoCategory?: string | null
  }>>([])
  const [rating, setRating] = useState<number>(0)

  useEffect(() => {
    if (!isOpen) {
      setFormData({})
      setFiles([])
      setRating(0)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (uploadState?.active) return
    const finalFiles = files.map((f) => {
      const newFile = new File([f.file], f.newName, { type: f.file.type || "application/octet-stream" })
      return {
        file: newFile,
        photoCategory: f.photoCategory || null,
      }
    })
    // Merge all data including files
    const submissionData = {
      ...formData,
      files: finalFiles
    }
    onSubmit(submissionData)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map(file => {
        const reader = new FileReader()
        const lastDotIndex = file.name.lastIndexOf('.')
        const nameWithoutExt = lastDotIndex > 0 ? file.name.substring(0, lastDotIndex) : file.name
        const ext = lastDotIndex > 0 ? file.name.substring(lastDotIndex) : ''

        const fileObj = {
          file,
          originalName: file.name,
          newName: file.name,
          nameWithoutExt, // Store name without extension
          extension: ext,   // Store extension separately
          preview: undefined as string | undefined
        }

        if (file.type.startsWith('image/')) {
          reader.onloadend = () => {
            fileObj.preview = reader.result as string
            setFiles(prev => [...prev])
          }
          reader.readAsDataURL(file)
        }

        return fileObj
      })
      setFiles(prev => [...prev, ...newFiles])
    }
  }

  const updateFileName = (index: number, newName: string) => {
    const file = files[index]
    const newFileName = `${newName}${file.extension}`
    setFiles(prev => prev.map((f, i) => i === index ? {
      ...f,
      nameWithoutExt: newName,
      newName: newFileName
    } : f))
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return '🖼️'
    if (fileType.startsWith('video/')) return '🎥'
    if (fileType.includes('pdf')) return '📄'
    if (fileType.includes('word') || fileType.includes('document')) return '📝'
    if (fileType.includes('excel') || fileType.includes('sheet')) return '📊'
    if (fileType.includes('zip') || fileType.includes('rar')) return '📦'
    return '📎'
  }

  const renderField = (field: any) => {
    switch (field.type) {
      case "textarea":
        return (
          <textarea
            value={formData[field.name] || ""}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            rows={4}
            required={field.required}
          />
        )

      case "file":
        return (
          <div>
            <input
              type="file"
              multiple
              accept={field.accept}
              onChange={handleFileChange}
              disabled={uploadState?.active}
              className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              required={field.required && files.length === 0}
            />
            {files.length > 0 && (
              <div className="mt-4 space-y-3">
                {files.map((fileObj, index) => (
                  <div key={index} className="border border-gray-200 rounded p-3 bg-gray-50">
                    <div className="flex items-start gap-3">
                      {fileObj.preview ? (
                        <img src={fileObj.preview} alt="preview" className="w-16 h-16 object-cover rounded" />
                      ) : (
                        <div className="w-16 h-16 flex items-center justify-center bg-gray-200 rounded text-2xl">
                          {getFileIcon(fileObj.file.type)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={fileObj.nameWithoutExt}
                            onChange={(e) => updateFileName(index, e.target.value)}
                            disabled={uploadState?.active}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="File name"
                          />
                          <span className="px-2 py-1 text-sm text-gray-600 bg-gray-100 border border-gray-300 rounded ">
                            {fileObj.extension}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Original: {fileObj.originalName} | Size: {(fileObj.file.size / 1024).toFixed(2)} KB
                        </p>
                        {field.enablePhotoCategory && (
                          <div className="mt-2">
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                              Photo Category
                            </label>
                            <select
                              value={fileObj.photoCategory || ""}
                              onChange={(e) =>
                                setFiles((prev) =>
                                  prev.map((fileItem, fileIndex) =>
                                    fileIndex === index
                                      ? { ...fileItem, photoCategory: e.target.value || null }
                                      : fileItem
                                  )
                                )
                              }
                              disabled={uploadState?.active}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select category</option>
                              <option value="indoor">Indoor</option>
                              <option value="outdoor">Outdoor</option>
                            </select>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        disabled={uploadState?.active}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )

      case "select":
        return (
          <select
            value={formData[field.name] || ""}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt: any) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )
      case "selectWithSearch":
        return (
          <SelectWithSearch2
            placeholder={`Select ${field.label}`}
            value={formData[field.name] || null}  // This should be a SelectOption object, not just a value
            onChange={(option) => {
              setFormData({
                ...formData,
                [field.name]: option,  // Store the entire option object, not just option.value
              })
            }}
            disabled={field.disabled}
            className="w-full"
            apiEndpoint={field.apiEndpoint}
            dataKey={field.dataKey}
            searchParam={field.searchParam}
            dependencyId={field.dependencyId}
            labelKey={field.labelKey}
            valueKey={field.valueKey}
            displayFormat={field.displayFormat}
            extraParams={field.requestParams}
            mapOption={field.mapOption}
          />
        )

      case "rating":
        return (
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => {
                  setRating(star)
                  setFormData({ ...formData, [field.name]: star })
                }}
                className={`text-2xl transition-colors ${star <= (formData[field.name] || rating)
                  ? "text-yellow-400"
                  : "text-gray-300"
                  } hover:text-yellow-400`}
              >
                ★
              </button>
            ))}
            <span className="ml-2 text-sm text-gray-600">
              {formData[field.name] || rating || 0} / 5
            </span>
          </div>
        )

      case "priority":
        return (
          <select
            value={formData[field.name] || ""}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            required={field.required}
          >
            <option value="">Select Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        )

      default:
        return (
          <input
            type={field.type}
            value={formData[field.name] || ""}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            required={field.required}
          />
        )
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={uploadState?.active ? undefined : onClose} />

      {/* Offcanvas */}
      <div className="overflow-y-auto fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          </div>
          <button onClick={onClose} disabled={uploadState?.active} className="p-2 hover:bg-gray-200 rounded transition-colors">
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        {/* Content */}
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {uploadState?.active && (
                <div className="rounded border border-blue-100 bg-blue-50 p-3">
                  <div className="flex items-center justify-between text-xs font-medium text-blue-700 mb-2">
                    <span>{uploadState.message}</span>
                    <span>{uploadState.percentage}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-blue-100 overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-200"
                      style={{ width: `${uploadState.percentage}%` }}
                    />
                  </div>
                </div>
              )}
              {fields.map((field) => {
                if (field.shouldShow && !field.shouldShow(formData)) {
                  return null
                }
                return (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {renderField(field)}
                  </div>
                )
              })}
            </form>
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={onClose}
                disabled={uploadState?.active}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={uploadState?.active}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                {uploadState?.active ? "Uploading..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Overview Tab Component
const OverviewTab = ({ siteData }: { siteData: SiteData }) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex justify-between items-center mb-4 pb-2">
        <h3 className="text-base font-semibold text-gray-900 border-gray-200">{title}</h3>
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Basic Information">
            <DetailRow label="Site ID" value={siteData.site_id} icon={FiFileText} />
            <DetailRow label="Site Name" value={siteData.site_name} icon={FiMapPin} />
            <DetailRow label="Site Address" value={siteData.site_address} icon={FiMapPin} />
            <DetailRow label="Project" value={siteData?.project?.project_name || 'N/A'} icon={FiActivity} />
            <DetailRow label="Activity" value={siteData?.activity?.activity_name || 'N/A'} icon={FiActivity} />
            <DetailRow label="Reference Number" value={siteData.reference_number} icon={FiFileText} />
            <DetailRow label="Status" value={siteData.status} icon={FiInfo} />
            <DetailRow label="Survey Workflow Status" value={siteData.survey_workflow_status} icon={FiInfo} />
            <DetailRow label="Site Priority" value={siteData.site_priority} icon={FiTarget} />
            <DetailRow label="OPCO Name" value={siteData.opco_name} icon={FiFileText} />
            <DetailRow label="OPCO ID" value={siteData.opco_id} icon={FiFileText} />
            <DetailRow label="Client Project Name" value={siteData.client_project_name} icon={FiFileText} />
            <DetailRow label="Client Allocation Date" value={formatDate(siteData.client_allocation_date)} icon={FiCalendar} />
          </Section>

          <Section title="Location Information">
            {siteData.state_circle && <DetailRow label="State" value={`${siteData.state_circle.state_name} ${siteData.state_circle.circle_name ? `(${siteData.state_circle.circle_name})` : ''} `} icon={FiMapPin} />}
            <DetailRow label="District" value={siteData?.district?.name} icon={FiMapPin} />
            <DetailRow label="Site Latitude" value={siteData.site_latitude} icon={FiMapPin} />
            <DetailRow label="Site Longitude" value={siteData.site_longitude} icon={FiMapPin} />
            {/* Where the engineer actually was when field work started — the
                site unlock point. Sits beside the planned site coordinates so
                the two can be read against each other. */}
            <DetailRow label="FW Start Latitude" value={siteData.site_unlock_latitude} icon={FiMapPin} />
            <DetailRow label="FW Start Longitude" value={siteData.site_unlock_longitude} icon={FiMapPin} />
            <DetailRow label="Location Verified" value={siteData.is_location_verified ? 'Yes' : 'No'} icon={siteData.is_location_verified ? FiCheckCircle : FiAlertCircle} />
            <DetailRow label="Location Verification Attempts" value={siteData.location_verification_attempts} icon={FiInfo} />
            <DetailRow label="Last Location Check" value={formatDate(siteData.last_location_check)} icon={FiClock} />
          </Section>

          <Section title="Customer Requirements & Site Information">
            <DetailRow label="Customer Requirement" value={siteData.customer_requirement} icon={FiInfo} />
            <DetailRow label="Site Information 1" value={siteData.site_information1} icon={FiInfo} />
            <DetailRow label="Site Information 2" value={siteData.site_information2} icon={FiInfo} />
            <DetailRow label="Site Information 3" value={siteData.site_information3} icon={FiInfo} />
          </Section>

          <Section title="Status Reasons">
            <DetailRow label="Cancel Reason" value={siteData.cancel_reason} icon={FiInfo} />
            <DetailRow label="Hold Reason" value={siteData.hold_reason} icon={FiInfo} />
          </Section>
        </div>
      </div>
    </div>
  );
};

// Site Files Section - shows all uploaded files categorized by upload_stage
const SiteFilesSection: React.FC<{ siteId: number }> = ({ siteId }) => {
  const [files, setFiles] = useState<SiteFile[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setLoading(true)
        const response = await axiosInstance.get(`/survey/sites/${siteId}/files`)
        setFiles(response.data.data.files || [])
      } catch (error) {
        console.error('Error fetching site files:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchFiles()
  }, [siteId])

  const stageLabels: Record<string, string> = {
    site_created: 'Site Created',
    engineer_assigned: 'Engineer Assigned',
    field_work_in_progress: 'Field Work In Progress',
    data_uploaded: 'Site Data Upload',
    field_work_completed: 'Field Work Completed',
    ta_review_completed: 'TA Review',
    report_maker_assigned: 'Report Maker',
    report_completed: 'Report Completed',
    validator_assigned: 'Validation',
    report_finalized: 'Report Finalized',
    report_sent_to_client: 'Sent to Client',
  }

  const grouped = files.reduce<Record<string, SiteFile[]>>((acc, file) => {
    const stage = file.upload_stage || 'other'
    if (!acc[stage]) acc[stage] = []
    acc[stage].push(file)
    return acc
  }, {})

  if (loading) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mt-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3">Uploaded Files</h3>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <FiRefreshCw className="animate-spin" size={14} />
          Loading files...
        </div>
      </div>
    )
  }

  if (files.length === 0) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mt-4">
        <h3 className="text-base font-semibold text-gray-900 mb-2">Uploaded Files</h3>
        <p className="text-sm text-gray-400 italic">No files uploaded yet</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mt-4">
      <h3 className="text-base font-semibold text-gray-900 mb-3">
        Uploaded Files ({files.length})
      </h3>
      <div className="space-y-4">
        {Object.entries(grouped).map(([stage, stageFiles]) => (
          <div key={stage}>
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 pb-1 border-b border-gray-100">
              {stageLabels[stage] || stage} ({stageFiles.length})
            </h4>
            <div className="space-y-1">
              {stageFiles.map((file) => (
                <div key={file.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50">
                  <FiUpload size={13} className="text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <a
                      href={file.file_path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline truncate block"
                      title={file.file_name}
                    >
                      {file.file_name}
                    </a>
                    <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                      {file.uploader && (
                        <span><FiUser className="inline" size={10} /> {file.uploader.first_name} {file.uploader.last_name}</span>
                      )}
                      {file.uploaded_at && (
                        <span><FiClock className="inline" size={10} /> {new Date(file.uploaded_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Workflow Tab Component
const WorkflowTab: React.FC<{ siteData: SiteData; onRefresh: () => void }> = ({ siteData, onRefresh }) => {
  const [stepDetailsOpen, setStepDetailsOpen] = useState(false)
  const [selectedStep, setSelectedStep] = useState<string>('')

  const openStepDetails = (status: string) => {
    setSelectedStep(status)
    setStepDetailsOpen(true)
  }

  const workflowSteps = [
    {
      stepNumber: 1,
      status: "site_created",
      label: "Site Created",
      completed: true,
      assignee: siteData?.creator ? `${siteData?.creator.first_name} ${siteData?.creator.last_name}` : null,
    },
    {
      stepNumber: 2,
      status: "engineer_assigned",
      label: "Engineer Assigned",
      completed: !!siteData.assigned_engineer_id || [
        "engineer_assigned",
        "field_work_in_progress",
        "data_uploaded",
        "field_work_completed",
        "ta_review_completed",
        "report_maker_assigned",
        "report_completed",
        "validator_assigned",
        "report_finalized",
        "report_sent_to_client",
      ].includes(siteData.survey_workflow_status),
      assignee: siteData.site_engineer
        ? `${siteData.site_engineer.first_name} ${siteData.site_engineer.last_name}`
        : null,
    },
    {
      stepNumber: 3,
      status: "field_work_in_progress",
      label: "Field Work Started",
      completed: [
        "field_work_in_progress",
        "data_uploaded",
        "field_work_completed",
        "ta_review_completed",
        "report_maker_assigned",
        "report_completed",
        "validator_assigned",
        "report_finalized",
        "report_sent_to_client",
      ].includes(siteData.survey_workflow_status),
      assignee: siteData.site_engineer
        ? `${siteData.site_engineer.first_name} ${siteData.site_engineer.last_name}`
        : null,
    },
    {
      stepNumber: 4,
      status: "data_uploaded",
      label: "Site Data WIP",
      completed: [
        "data_uploaded",
        "field_work_completed",
        "ta_review_completed",
        "report_maker_assigned",
        "report_completed",
        "validator_assigned",
        "report_finalized",
        "report_sent_to_client",
      ].includes(siteData.survey_workflow_status),
      assignee: siteData.site_engineer
        ? `${siteData.site_engineer.first_name} ${siteData.site_engineer.last_name}`
        : null,
    },
    {
      stepNumber: 5,
      status: "field_work_completed",
      label: "Field Work Completed",
      completed: [
        "field_work_completed",
        "ta_review_completed",
        "report_maker_assigned",
        "report_completed",
        "validator_assigned",
        "report_finalized",
        "report_sent_to_client",
      ].includes(siteData.survey_workflow_status),
      assignee: siteData.site_engineer
        ? `${siteData.site_engineer.first_name} ${siteData.site_engineer.last_name}`
        : null,
    },
    {
      stepNumber: 6,
      status: "ta_review_completed",
      label: "Technical Associate Review Completed",
      completed: [
        "ta_review_completed",
        "report_maker_assigned",
        "report_completed",
        "validator_assigned",
        "report_finalized",
        "report_sent_to_client",
      ].includes(siteData.survey_workflow_status),
      assignee: siteData.ta ? `${siteData.ta.first_name} ${siteData.ta.last_name}` : null,
    },
    {
      stepNumber: 7,
      status: "report_maker_assigned",
      label: "Report Maker Assigned",
      completed: !!siteData.assigned_report_maker_id || [
        "report_maker_assigned",
        "report_completed",
        "validator_assigned",
        "report_finalized",
        "report_sent_to_client",
      ].includes(siteData.survey_workflow_status),
      assignee: siteData.report_designer
        ? `${siteData.report_designer.first_name} ${siteData.report_designer.last_name}`
        : null,
    },
    {
      stepNumber: 8,
      status: "report_completed",
      label: "Report Completed",
      completed: ["report_completed", "validator_assigned", "report_finalized", "report_sent_to_client"].includes(
        siteData.survey_workflow_status,
      ),
      assignee: siteData.report_designer
        ? `${siteData.report_designer.first_name} ${siteData.report_designer.last_name}`
        : null,
    },
    {
      stepNumber: 9,
      status: "validator_assigned",
      label: "Validator Assigned",
      completed: !!siteData.assigned_validator_id || ["validator_assigned", "report_finalized", "report_sent_to_client"].includes(
        siteData.survey_workflow_status,
      ),
      assignee: siteData.validator ? `${siteData.validator.first_name} ${siteData.validator.last_name}` : null,
    },
    {
      stepNumber: 10,
      status: "report_finalized",
      label: "Report Finalized",
      completed: ["report_finalized", "report_sent_to_client"].includes(siteData.survey_workflow_status),
      assignee: siteData.validator ? `${siteData.validator.first_name} ${siteData.validator.last_name}` : null,
    },
    {
      stepNumber: 11,
      status: "report_sent_to_client",
      label: "Sent to Client",
      completed: siteData.survey_workflow_status === "report_sent_to_client",
      assignee: siteData.mis ? `${siteData.mis.first_name} ${siteData.mis.last_name}` : null,
    },
  ]

  return (
    <div className="">
      {/* Workflow Progress */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-base font-semibold text-gray-900">Workflow Progress</h3>
        </div>

        <div className="space-y-3">
          {workflowSteps.map((step) => {
            return (
              <div key={step.status} className="border border-gray-200 rounded overflow-hidden">
                <button
                  onClick={() => openStepDetails(step.status)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step.completed ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                      }`}
                  >
                    {step.completed ? <FiCheckCircle size={16} /> : <FiClock size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${step.completed ? "text-gray-900" : "text-gray-500"}`}>
                      {step.label}
                    </p>
                    {step.assignee && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        <FiUser className="inline mr-1" size={10} />
                        {step.assignee}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    {siteData.survey_workflow_status === step.status && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-900 text-xs font-medium rounded-full">Current</span>
                    )}
                    <FiChevronRight className="text-gray-400" size={16} />
                  </div>
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Step Details Offcanvas */}
      <StepDetailsOffcanvas
        isOpen={stepDetailsOpen}
        onClose={() => setStepDetailsOpen(false)}
        siteId={siteData.id}
        status={selectedStep}
      />
    </div>
  )
}

// Billing Tab Component
const BillingTab: React.FC<{ siteData: SiteData }> = ({ siteData }) => {
  const auth = useAuth()

  if (!auth.hasPermission('Billing', 'view_stats')) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-8 text-center">
        <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-600">You don't have permission to view billing information.</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* PR Details */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">PR Details</h3>
        <DetailRow label="PR Number" value={siteData.pr_number} icon={FiHash} />
        <DetailRow label="PR Date" value={siteData.pr_date ? formatDate(siteData.pr_date) : 'N/A'} icon={FiCalendar} />
      </div>

      {/* PO Details */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">PO Details</h3>
        <DetailRow label="PO Number" value={siteData.po_number} icon={FiHash} />
        <DetailRow label="PO Date" value={siteData.po_date ? formatDate(siteData.po_date) : 'N/A'} icon={FiCalendar} />
        <DetailRow label="PO Shared at Allocation" value={siteData.is_po_no_shared_at_allocation ? 'Yes' : 'No'} icon={FiCheckCircle} />
      </div>

      {/* Contact Details */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">Billing Contact</h3>
        <DetailRow label="Contact Person" value={siteData.contact_person} icon={FiUser} />
        <DetailRow label="Contact Number" value={siteData.contact_number} icon={FiPhone} />
      </div>
    </div>
  )
}

// Requirements Tab Component
interface ProjectRequirement {
  id: number
  title: string
  content: string
  requirement_type: 'project' | 'activity' | 'common'
  project_id: number | null
  activity_id: number | null
  created_at: string
  updated_at: string
  created_by: number
  updated_by: number | null
  project?: {
    id: number
    project_name: string
    project_short_name?: string
  }
  activity?: {
    id: number
    activity_name: string
  }
  creator?: {
    id: number
    first_name: string
    last_name: string
  }
  updater?: {
    id: number
    first_name: string
    last_name: string
  }
}

interface ExpandedState {
  [key: number]: boolean
}

const RequirementsTab: React.FC<{ siteData: SiteData; showManageMode?: boolean }> = ({ siteData, showManageMode = false }) => {
  const { confirm } = useConfirm()
  const [requirements, setRequirements] = useState<ProjectRequirement[]>([])
  const [loading, setLoading] = useState(true)

  // Edit modal state (replaces inline expand)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingReq, setEditingReq] = useState<ProjectRequirement | null>(null)
  const [editTitle, setEditTitle] = useState("")
  const [editContent, setEditContent] = useState("")

  // See more modal state
  const [seeMoreModalOpen, setSeeMoreModalOpen] = useState(false)
  const [seeMoreContent, setSeeMoreContent] = useState<{ title: string; content: string } | null>(null)

  const [isCreating, setIsCreating] = useState(false)
  const [newTitle, setNewTitle] = useState("")
  const [newContent, setNewContent] = useState("")
  const [newType, setNewType] = useState<'project' | 'activity' | 'common'>('common')
  const [submitting, setSubmitting] = useState(false)

  const auth = useAuth()

  useEffect(() => {
    fetchRequirements()
  }, [siteData.id])

  const fetchRequirements = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get(`/requirements/for-site/${siteData.id}`)
      if (response.data.success) {
        const data = response.data.data?.requirements
        setRequirements(Array.isArray(data) ? data : [])
      }
    } catch (error) {
      console.error("Error fetching requirements:", error)
      toast.error("Failed to load requirements")
      setRequirements([])
    } finally {
      setLoading(false)
    }
  }

  const handleStartEdit = (req: ProjectRequirement) => {
    setEditingReq(req)
    setEditTitle(req.title)
    setEditContent(req.content)
    setEditModalOpen(true)
  }

  const handleSaveEdit = async (id: number) => {
    if (!editTitle.trim() || !editContent.trim()) {
      toast.error("Title and Content are required")
      return
    }

    try {
      setSubmitting(true)
      const response = await axiosInstance.put(`/requirements/${id}`, {
        title: editTitle.trim(),
        content: editContent.trim()
      })
      if (response.data.success) {
        toast.success("Requirement updated successfully")
        setEditModalOpen(false)
        setEditingReq(null)
        fetchRequirements()
      }
    } catch (error) {
      console.error("Error updating requirement:", error)
      toast.error("Failed to update requirement")
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: "Delete requirement",
      message: "Delete this requirement? This cannot be undone.",
      confirmText: "Delete requirement",
      variant: "danger",
    })
    if (!ok) return

    try {
      const response = await axiosInstance.delete(`/requirements/${id}`)
      if (response.data.success) {
        toast.success("Requirement deleted successfully")
        fetchRequirements()
      }
    } catch (error) {
      console.error("Error deleting requirement:", error)
      toast.error("Failed to delete requirement")
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim() || !newContent.trim()) {
      toast.error("Title and Content are required")
      return
    }

    try {
      setSubmitting(true)

      const payload: any = {
        title: newTitle.trim(),
        content: newContent.trim(),
        requirement_type: newType,
      }

      if (newType === 'project') {
        payload.project_id = siteData.project_id
      } else if (newType === 'activity') {
        payload.activity_id = siteData.activity?.id
      }

      const response = await axiosInstance.post('/requirements', payload)
      if (response.data.success) {
        toast.success("Requirement created successfully")
        setIsCreating(false)
        setNewTitle("")
        setNewContent("")
        setNewType('common')
        fetchRequirements()
      }
    } catch (error) {
      console.error("Error creating requirement:", error)
      toast.error("Failed to create requirement")
    } finally {
      setSubmitting(false)
    }
  }

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'project':
        return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">Project Specific</span>
      case 'activity':
        return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">Activity Specific</span>
      case 'common':
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">Common Client Requirement</span>
    }
  }

  // Helper function to count lines in text
  const countLines = (text: string): number => {
    return text.split('\n').length
  }

  // Helper function to get truncated text (5 lines max)



  if (loading) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-8 text-center">
        <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 font-medium">Loading site requirements...</p>
      </div>
    )
  }

  const canViewProjectReqs  = auth.hasPermission("Site", "req_view_project")
  const canViewActivityReqs = auth.hasPermission("Site", "req_view_activity")
  const canViewCommonReqs   = auth.hasPermission("Site", "req_view_common")
  const canViewAny = canViewProjectReqs || canViewActivityReqs || canViewCommonReqs

  if (!canViewAny) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-8 text-center">
        <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-600">You don't have permission to view requirements.</p>
      </div>
    )
  }

  const canEdit   = canViewAny   // view permission also allows edit/delete for now
  const canCreate = canViewAny
  const canDelete = canViewAny

  // In manage mode (offcanvas), always show create/edit/delete if permitted
  // In tab mode, show read-only view with edit for permitted users
  const showCreateButton = canCreate && !isCreating

  return (
    <div className="space-y-6">

      {isCreating && (
        <form onSubmit={handleCreate} className="bg-white p-5 rounded shadow-sm border-2 border-blue-200 space-y-4 transition-all">
          <div className="flex justify-between items-center border-b border-gray-100 pb-3">
            <h4 className="font-bold text-gray-900 text-base">New Client Requirement</h4>
            <button
              type="button"
              onClick={() => setIsCreating(false)}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition-all"
            >
              <FiX size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Requirement Scope / Type</label>
              <select
                value={newType}
                onChange={(e: any) => setNewType(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="common">Common Client (All Sites)</option>
                {siteData.project_id && (
                  <option value="project">Project Specific ({siteData.project?.project_name || "This Project"})</option>
                )}
                {siteData.activity?.id && (
                  <option value="activity">Activity Specific ({siteData.activity?.activity_name || "This Activity"})</option>
                )}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter a descriptive title"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Instruction Content</label>
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Provide exact requirement details..."
              rows={4}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setIsCreating(false)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded text-sm transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-800 hover:bg-blue-900 text-white font-semibold px-4 py-2 rounded text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                "Save Requirement"
              )}
            </button>
          </div>
        </form>
      )}

      {!requirements || requirements.length === 0 ? (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-12 text-center">
          <FiFileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-base font-bold text-gray-900 mb-1">No Requirements Found</h4>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            There are no requirements assigned to this project or activity, and no common guidelines are registered.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.isArray(requirements) && requirements
            .filter((req) => {
              // Show only cards whose type matches the user's view permission
              if (req.requirement_type === 'project')  return canViewProjectReqs
              if (req.requirement_type === 'activity') return canViewActivityReqs
              if (req.requirement_type === 'common')   return canViewCommonReqs
              return false
            })
            .map((req) => {
            // Regular card view — no inline expansion
            return (
              <div key={req.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all flex flex-col">
                {/* Card Header */}
                <div className="p-4 pb-2">
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-bold text-gray-900 text-sm flex-1 break-words">
                      {req.title}
                    </h4>
                    
                  </div>

                  {/* Type Badge */}
                  <div className="mt-2">
                    {req.requirement_type === 'project' && (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">Project Specific</span>
                    )}
                    {req.requirement_type === 'activity' && (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">Activity Specific</span>
                    )}
                    {req.requirement_type === 'common' && (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">Common</span>
                    )}
                  </div>

                  {/* Project/Activity info */}
                  {req.requirement_type === 'project' && req.project && (
                    <p className="text-xs text-blue-600 font-medium mt-1">Project: {req.project.project_name}</p>
                  )}
                  {req.requirement_type === 'activity' && req.activity && (
                    <p className="text-xs text-emerald-600 font-medium mt-1">Activity: {req.activity.activity_name}</p>
                  )}
                </div>

                {/* Card Content */}
                <div className="px-4 py-2">
                  {countLines(req.content) > 5 ? (
                    <>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                        {req.content}
                      </p>
                    
                    </>
                  ) : (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                      {req.content}
                    </p>
                  )}
                </div>

                {/* Card Footer */}
                <div className="px-4 py-3 mt-auto border-t border-gray-100">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500">
                    {req.updater ? (
                      <span className="flex items-center gap-1">
                        <FiUser size={11} className="text-gray-400" />
                        Updated by <strong className="text-gray-700">{req.updater.first_name} {req.updater.last_name}</strong>
                      </span>
                    ) : req.creator ? (
                      <span className="flex items-center gap-1">
                        <FiUser size={11} className="text-gray-400" />
                        Created by <strong className="text-gray-700">{req.creator.first_name} {req.creator.last_name}</strong>
                      </span>
                    ) : null}

                    <span className="flex items-center gap-1">
                      <FiCalendar size={11} className="text-gray-400" />
                      <strong className="text-gray-700">{formatDate(req.updated_at || req.created_at)}</strong>
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Edit Requirement Popup Modal ── */}
      {editModalOpen && editingReq && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => { setEditModalOpen(false); setEditingReq(null) }}
          >
            {/* Modal — stop click propagation so clicking inside doesn't close */}
            <div
              className="bg-white rounded-lg shadow-2xl w-full max-w-lg flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900 rounded-t-lg flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <FiEdit size={18} />
                    Edit Requirement
                  </h3>
                  <p className="text-xs text-blue-200 mt-0.5 truncate max-w-xs">{editingReq.title}</p>
                </div>
                <button
                  onClick={() => { setEditModalOpen(false); setEditingReq(null) }}
                  className="text-white hover:text-gray-200 transition-colors p-1"
                >
                  <FiX size={22} />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4">
                {/* Type badge (read-only) */}
                <div className="flex items-center gap-2">
                  {editingReq.requirement_type === 'project' && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">Project Specific</span>
                  )}
                  {editingReq.requirement_type === 'activity' && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">Activity Specific</span>
                  )}
                  {editingReq.requirement_type === 'common' && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">Common</span>
                  )}
                  {editingReq.project && (
                    <span className="text-xs text-blue-600 font-medium">{editingReq.project.project_name}</span>
                  )}
                  {editingReq.activity && (
                    <span className="text-xs text-emerald-600 font-medium">{editingReq.activity.activity_name}</span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Enter requirement title"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Content <span className="text-red-500">*</span></label>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={5}
                    placeholder="Describe the requirement in detail..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setEditModalOpen(false); setEditingReq(null) }}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveEdit(editingReq.id)}
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-medium text-white bg-blue-800 rounded-lg hover:bg-blue-900 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── See More Content Modal ── */}
      {seeMoreModalOpen && seeMoreContent && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => { setSeeMoreModalOpen(false); setSeeMoreContent(null) }}
          >
            {/* Modal */}
            <div
              className="bg-white rounded-lg shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900 rounded-t-lg flex items-center justify-between flex-shrink-0">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <FiFileText size={18} />
                    Requirement Details
                  </h3>
                  <p className="text-xs text-blue-200 mt-1 truncate">{seeMoreContent.title}</p>
                </div>
                <button
                  onClick={() => { setSeeMoreModalOpen(false); setSeeMoreContent(null) }}
                  className="text-white hover:text-gray-200 transition-colors p-1 flex-shrink-0"
                >
                  <FiX size={22} />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 overflow-y-auto flex-1">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Title</h4>
                  <p className="text-base text-gray-900 font-medium mb-4 break-words">{seeMoreContent.title}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Full Content</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
                      {seeMoreContent.content}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end flex-shrink-0">
                <button
                  type="button"
                  onClick={() => { setSeeMoreModalOpen(false); setSeeMoreContent(null) }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded-lg hover:bg-blue-900 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// History Tab Component
const HistoryTab: React.FC<{ siteId: number }> = ({ siteId }) => {
  const [workflowHistory, setWorkflowHistory] = useState<WorkflowLog[]>([])
  const [loading, setLoading] = useState(true)
  const auth = useAuth()
  useEffect(() => {
    fetchWorkflowHistory()
  }, [siteId])

  const fetchWorkflowHistory = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get(`/survey/sites/${siteId}/workflow-history`)
      setWorkflowHistory(response.data.data.logs)
    } catch (error) {
      console.error("Error fetching workflow history:", error)
    } finally {
      setLoading(false)
    }
  }

  const getActionIcon = (actionType: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      status_change: <FiRefreshCw size={16} />,
      assignment: <FiUserCheck size={16} />,
      data_upload: <FiUpload size={16} />,
      file_upload: <FiUpload size={16} />,
      approval: <FiCheckCircle size={16} />,
      rejection: <FiXCircle size={16} />,
      location_verification: <FiMapPin size={16} />,
    }
    return iconMap[actionType] || <FiActivity size={16} />
  }

  const getActionColor = (actionType: string) => {
    const colorMap: Record<string, string> = {
      status_change: "bg-blue-100 text-blue-900",
      assignment: "bg-purple-100 text-purple-700",
      data_upload: "bg-green-100 text-green-700",
      file_upload: "bg-green-100 text-green-700",
      approval: "bg-green-100 text-green-700",
      rejection: "bg-red-100 text-red-700",
      location_verification: "bg-yellow-100 text-yellow-700",
    }
    return colorMap[actionType] || "bg-gray-100 text-gray-700"
  }

  if (loading) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4 p-4p-8 text-center">
        <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Loading history...</p>
      </div>
    )
  }

  if (!auth.hasPermission("Site", "history")) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4 p-4p-8 text-center">
        <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-600">You don't have permission to view workflow history.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Workflow History</h3>

        {workflowHistory.length === 0 ? (
          <div className="text-center py-8">
            <FiClock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No workflow history available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {workflowHistory.map((log) => (
              <div key={log.id} className="border border-gray-200 rounded p-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActionColor(log.action_type)}`}
                  >
                    {getActionIcon(log.action_type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action_type)}`}
                      >
                        {log.action_type.replace("_", " ").toUpperCase()}
                      </span>
                      {log.from_status && log.to_status && (
                        <span className="text-xs text-gray-500 capitalize">
                          {log.from_status.replace(/_/g, " ")} <FiArrowRight className="inline" size={12} /> {log.to_status.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-900 mb-1">{log.remarks}</p>

                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FiUser size={12} />
                        {log.performer.first_name} {log.performer.last_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <FiCalendar size={12} />
                        {formatDate(log.created_at)}
                      </span>
                    </div>

                    {/* {log.metadata && (
                      <div className=" p-2 bg-gray-50 rounded text-xs">
                        <pre className="text-gray-700 whitespace-pre-wrap">{JSON.stringify(log.metadata, null, 2)}</pre>
                      </div>
                    )} */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Site Members Tab Component
const SiteMembersTab: React.FC<{ siteData: SiteData }> = ({ siteData }) => {
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignType, setReassignType] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const { hasPermission, hidesSiteEngineer } = useAuth();
  const hasTcAccess = hasPermission("Site", 're_assign') || hasPermission("Site", 'assign_tc');
  const hasMisAccess = hasPermission("Site", 'mis');
  const canManageAssignments = hasTcAccess || hasMisAccess;
  const canReassignEngineer = hasTcAccess;
  const canReassignTA = hasTcAccess;
  const canReassignMIS = hasTcAccess || hasMisAccess;
  const canReassignReportMaker = hasTcAccess || hasMisAccess;
  const canReassignValidator = hasTcAccess || hasMisAccess;
  const stage = siteData.survey_workflow_status;
  const canReassignForStage = (type: string) => {
    if (siteData.status === 'cancelled' || stage === 'report_sent_to_client') return false;
    const completedByRole: Record<string, string[]> = {
      site_engineer: [
        'field_work_completed', 'ta_review_pending', 'ta_review_completed', 'ta_rejected',
        'mis_report_assign_pending', 'report_maker_assigned', 'report_in_progress',
        'report_completed', 'report_maker_rejected', 'validator_assigned',
        'validation_in_progress', 'validation_completed', 'validation_rejected',
        'report_finalized', 'report_sent_to_client',
      ],
      ta: [
        'ta_review_completed', 'mis_report_assign_pending', 'report_maker_assigned',
        'report_in_progress', 'report_completed', 'report_maker_rejected',
        'validator_assigned', 'validation_in_progress', 'validation_completed',
        'validation_rejected', 'report_finalized', 'report_sent_to_client',
      ],
      report_maker: [
        'report_completed', 'validator_assigned', 'validation_in_progress',
        'validation_completed', 'validation_rejected', 'report_finalized',
        'report_sent_to_client',
      ],
      validator: ['report_finalized', 'report_sent_to_client'],
    };
    return !(completedByRole[type] || []).includes(stage);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleIdForReassignType = (type: string): number[] => {
    switch (type) {
      case 'site_engineer': return SITE_ENGINEER_ASSIGNABLE_ROLE_IDS;
      case 'ta': return [5];
      case 'mis': return [6];
      case 'report_maker': return [9];
      case 'validator': return [10, 5, 4, 6, 3];
      default: return [];
    }
  };

  const handleReassignClick = (type: string) => {
    setReassignType(type);
    setShowReassignModal(true);
    setSelectedUser(null);
    setRemarks('');
  };

  const handleReassignSubmit = async () => {
    if (!selectedUser) { toast.error('Please select a user'); return; }
    try {
      setSaving(true);
      const payload: any = { site_ids: [siteData.id] };
      switch (reassignType) {
        case 'site_engineer': payload.site_engineer_userid = selectedUser.value; break;
        case 'ta': payload.ta_userid = selectedUser.value; break;
        case 'mis': payload.mis_userid = selectedUser.value; break;
        case 'report_maker': payload.report_designer_userid = selectedUser.value; break;
        case 'validator': payload.validator_userid = selectedUser.value; break;
      }
      await axiosInstance.put('/survey/sites/update-assignment', payload);
      toast.success('User reassigned successfully!');
      setShowReassignModal(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to reassign user');
    } finally {
      setSaving(false);
    }
  };

  const Section: React.FC<{ title: string; children: React.ReactNode; showReassign?: boolean; reassignType?: string; canReassign?: boolean; }> = ({ title, children, showReassign, reassignType: rt, canReassign }) => (
    <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex justify-between items-center mb-4 pb-2">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {showReassign && rt && (canReassign !== undefined ? canReassign : canManageAssignments) && (
          <button
            onClick={() => handleReassignClick(rt)}
            className="flex gap-2 items-center font-semibold text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
          >
            <FaUser /> Reassign
          </button>
        )}
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Contact Information">
            <DetailRow label="Technician Name" value={siteData.technician_name} icon={FiUser} />
            <DetailRow label="Technician Number" value={siteData.technician_number} icon={FiPhone} />
            <DetailRow label="FSE Name" value={siteData.fse_name} icon={FiUser} />
            <DetailRow label="FSE Number" value={siteData.fse_number} icon={FiPhone} />
            <DetailRow label="AOM Name" value={siteData.aom_name} icon={FiUser} />
            <DetailRow label="AOM Number" value={siteData.aom_number} icon={FiPhone} />
          </Section>

          <Section title="Team Coordinator Details">
            <DetailRow label="Username" value={siteData?.team_coordinator?.username || 'N/A'} icon={FiUser} />
            <DetailRow label="Name" value={(siteData?.team_coordinator?.first_name && siteData?.team_coordinator?.last_name) ? siteData?.team_coordinator?.first_name + " " + siteData?.team_coordinator?.last_name : 'N/A'} icon={FiUser} />
          </Section>

          {/* Field-work details minus the engineer's identity for vendor report
              makers — they still see the dates, status and remarks, just not who. */}
          <Section title={hidesSiteEngineer ? "Field Work Details" : "Site Engineer Details"}
            showReassign={!hidesSiteEngineer && canReassignForStage('site_engineer') && !!siteData.site_engineer_userid}
            reassignType="site_engineer"
            canReassign={canReassignEngineer}
          >
            {!hidesSiteEngineer && (
              <>
                <DetailRow label="Username" value={siteData.site_engineer_username} icon={FiUser} />
                <DetailRow label="Assign Date" value={formatDate(siteData.site_engineer_assign_date)} icon={FiCalendar} />
                <DetailRow label="Assign Time" value={siteData.site_engineer_assign_time} icon={FiClock} />
              </>
            )}
            <DetailRow label="Work Date" value={formatDate(siteData.site_engineer_work_date)} icon={FiCalendar} />
            <DetailRow label="Work Status" value={siteData.site_engineer_work_status} icon={FiInfo} />
            <DetailRow label="Work Remarks" value={siteData.site_engineer_work_remarks} icon={FiFileText} />
            <DetailRow label="Visit Status" value={siteData.site_engineer_visit_status} icon={FiInfo} />
            <DetailRow label="Visit Remarks" value={siteData.visit_remarks} icon={FiFileText} />
            <DetailRow label="Site Unlock Time" value={formatDate(siteData.site_unlock_time)} icon={FiClock} />
          </Section>

          <Section title="Technical Associate Details"
            showReassign={canReassignForStage('ta') && !!siteData.ta_userid}
            reassignType="ta"
            canReassign={canReassignTA}
          >
            <DetailRow label="Username" value={siteData.ta_username} icon={FiUser} />
            <DetailRow label="Work Date" value={formatDate(siteData.ta_work_date)} icon={FiCalendar} />
            <DetailRow label="Remarks" value={siteData.ta_remarks} icon={FiFileText} />
            <DetailRow label="SE Quality Feedback" value={siteData.ta_se_quality_feedback} icon={FiClipboard} />
          </Section>

          <Section title="MIS Details"
            showReassign={canReassignForStage('mis') && !!siteData.mis_userid}
            reassignType="mis"
            canReassign={canReassignMIS}
          >
            <DetailRow label="Username" value={siteData.mis_username} icon={FiUser} />
            <DetailRow label="Submission Date" value={formatDate(siteData.mis_submission_date)} icon={FiCalendar} />
            <DetailRow label="Remarks" value={siteData.mis_remarks} icon={FiFileText} />
            <DetailRow label="Validator QA Feedback" value={siteData.mis_validator_qa_feedback} icon={FiClipboard} />
          </Section>

          <Section title="Report Maker Details"
            showReassign={canReassignForStage('report_maker') && !!siteData.report_designer_userid}
            reassignType="report_maker"
            canReassign={canReassignReportMaker}
          >
            <DetailRow label="Username" value={siteData.report_designer_username} icon={FiUser} />
            <DetailRow label="Work Date" value={formatDate(siteData.report_designer_work_date)} icon={FiCalendar} />
            <DetailRow label="Remarks" value={siteData.report_designer_remarks} icon={FiFileText} />
          </Section>

          <Section title="Validator Details"
            showReassign={canReassignForStage('validator') && !!siteData.validator_userid}
            reassignType="validator"
            canReassign={canReassignValidator}
          >
            <DetailRow label="Username" value={siteData.validator_username} icon={FiUser} />
            <DetailRow label="Work Date" value={formatDate(siteData.validator_work_date)} icon={FiCalendar} />
            <DetailRow label="Remarks" value={siteData.validator_remarks} icon={FiFileText} />
            <DetailRow label="Report Maker QA Feedback" value={siteData.validator_report_designer_qa_feedback} icon={FiClipboard} />
          </Section>

          <div className="lg:col-span-2">
            <Section title="Team Members">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {siteData.site_engineer && (
                  <div className="p-3 bg-blue-50 rounded">
                    <h4 className="font-bold border-b text-sm pb-2 text-blue-900">Site Engineer</h4>
                    <DetailRow label="Username" value={siteData.site_engineer.username} />
                    <DetailRow label="Name" value={`${siteData.site_engineer.first_name} ${siteData.site_engineer.last_name}`} />
                  </div>
                )}
                {siteData.ta && (
                  <div className="p-3 bg-purple-50 rounded">
                    <h4 className="font-bold border-b text-sm pb-2 text-purple-900">Technical Associate</h4>
                    <DetailRow label="Username" value={siteData.ta.username} />
                    <DetailRow label="Name" value={`${siteData.ta.first_name} ${siteData.ta.last_name}`} />
                  </div>
                )}
                {siteData.mis && (
                  <div className="p-3 bg-orange-50 rounded">
                    <h4 className="font-bold border-b text-sm pb-2 text-orange-900">MIS</h4>
                    <DetailRow label="Username" value={siteData.mis.username} />
                    <DetailRow label="Name" value={`${siteData.mis.first_name} ${siteData.mis.last_name}`} />
                  </div>
                )}
                {siteData.report_designer && (
                  <div className="p-3 bg-green-50 rounded">
                    <h4 className="font-bold border-b text-sm pb-2 text-green-900">Report Maker</h4>
                    <DetailRow label="Username" value={siteData.report_designer.username} />
                    <DetailRow label="Name" value={`${siteData.report_designer.first_name} ${siteData.report_designer.last_name}`} />
                  </div>
                )}
                {siteData.validator && (
                  <div className="p-3 bg-indigo-50 rounded">
                    <h4 className="font-bold border-b text-sm pb-2 text-indigo-900">Validator</h4>
                    <DetailRow label="Username" value={siteData.validator.username} />
                    <DetailRow label="Name" value={`${siteData.validator.first_name} ${siteData.validator.last_name}`} />
                  </div>
                )}
              </div>
            </Section>
          </div>
        </div>
      </div>

      {showReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Reassign <span className="uppercase">{reassignType.replace(/_/g, ' ')}</span>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select User</label>
                <SelectWithSearch2
                  placeholder={`Select ${reassignType.replace(/_/g, ' ')}`}
                  value={selectedUser}
                  onChange={(option) => setSelectedUser(option)}
                  apiEndpoint="/survey/sites/assignment/users"
                  extraParams={{
                    role_id: getRoleIdForReassignType(reassignType),
                    project_id: siteData.project_id,
                    src: 'all',
                  }}
                  mapOption={(u: any) => ({
                    id: u.id,
                    value: u.id,
                    label: `${u.first_name} ${u.last_name} (${u.username}) - ${u?.employee_code || 'N/A'}`,
                  })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks (Optional)</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter reassignment remarks..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowReassignModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleReassignSubmit}
                className="flex-1 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors disabled:opacity-50"
                disabled={saving || !selectedUser}
              >
                {saving ? 'Reassigning...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface SlideshowState {
  slug: string | null
  expiry_at: string | null
  is_expired: boolean
  generated_by: { first_name: string; last_name: string; username: string } | null
  last_regenerated_at: string | null
  created_at: string | null
}

const formatSlideshowGeneratedBy = (
  user?: { first_name?: string; last_name?: string; username?: string } | null
) => {
  if (!user) return "Unknown user"
  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
  return fullName || user.username || "Unknown user"
}

// Main SiteDetails Component
const SiteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState<string>(''); // resolved after permissions load
  const [siteData, setSiteData] = useState<SiteData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalState, setModalState] = useState<{ isOpen: boolean; action: 'hold' | 'cancel' | 'resume' | null }>({
    isOpen: false,
    action: null
  });

  // Step details (for header shortcut button)
  const [headerStepDetailsOpen, setHeaderStepDetailsOpen] = useState(false)
  const [headerSelectedStep, setHeaderSelectedStep] = useState<string>('')

  const openStepDetails = (status: string) => {
    setHeaderSelectedStep(status)
    setHeaderStepDetailsOpen(true)
  }

  // Uploaded Data offcanvas state
  const [showUploadedData, setShowUploadedData] = useState(false)

  // Slideshow state
  const [slideshowData, setSlideshowData] = useState<SlideshowState | null>(null)
  const [slideshowLoading, setSlideshowLoading] = useState(false)
  const [showSlideshowModal, setShowSlideshowModal] = useState(false)
  const [slideshowForm, setSlideshowForm] = useState({ expiry_days: 7 })

  // Header actions are permission-gated: Slideshow, Field Data, Uploaded Data
  // and the download-everything ZIP each have their own Site permission.
  const canViewSlideshow = auth.hasPermission("Site", "slideshow")
  const canViewFieldData = auth.hasPermission("Site", "field_data")
  const canViewUploadedData = auth.hasPermission("Site", "uploaded_data")
  const canDownloadAllFiles = auth.hasPermission("Site", "download_all_files")

  // One-click ZIP of every file on the site, across all upload stages. Files are
  // fetched through short-lived presigned URLs and zipped in the browser, the
  // same way the per-stage download already works — nothing is staged server-side.
  const [downloadingAll, setDownloadingAll] = useState(false)
  const handleDownloadAllFiles = async () => {
    if (downloadingAll || !siteData) return
    setDownloadingAll(true)
    try {
      const response = await axiosInstance.get(`/survey/sites/${id}/all-files/presigned-urls`)
      const files: Array<{ url: string; fileName: string; upload_stage?: string }> =
        response.data?.data?.files || []
      if (!files.length) {
        toast.error("No files to download")
        return
      }

      const JSZip = (await import("jszip")).default
      const zip = new JSZip()
      // Foldered by upload stage, with a per-folder counter so duplicate file
      // names from different uploads never collide.
      const usedPerFolder = new Map<string, number>()

      await Promise.all(
        files.map(async (file) => {
          try {
            const resp = await fetch(file.url)
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
            const blob = await resp.blob()
            const folder = (file.upload_stage || "other").replace(/[^a-zA-Z0-9_-]+/g, "-")
            const baseName = (file.fileName || "file").split(/[\\/]/).pop() || "file"
            const key = `${folder}/${baseName.toLowerCase()}`
            const seen = usedPerFolder.get(key) || 0
            usedPerFolder.set(key, seen + 1)
            const dotIdx = baseName.lastIndexOf(".")
            const ext = dotIdx >= 0 ? baseName.slice(dotIdx) : ""
            const stem = dotIdx >= 0 ? baseName.slice(0, dotIdx) : baseName
            const finalName = seen === 0 ? baseName : `${stem}_${seen}${ext}`
            zip.file(`${folder}/${finalName}`, blob)
          } catch (err) {
            console.error(`Skipping file ${file.fileName}:`, err)
          }
        }),
      )

      const content = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 9 } })
      const blobUrl = window.URL.createObjectURL(content)
      const link = document.createElement("a")
      link.href = blobUrl
      link.download = `${siteData.site_id}-all-files.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      toast.success(`Downloaded ${files.length} file(s)`)
    } catch (error: any) {
      console.error("Download all files failed:", error)
      toast.error(error?.response?.data?.message || "Failed to download files")
    } finally {
      setDownloadingAll(false)
    }
  }

  // Workflow action modal state (lifted from WorkflowTab so button shows in page header)
  const [workflowActionModalOpen, setWorkflowActionModalOpen] = useState(false)
  const [workflowCurrentAction, setWorkflowCurrentAction] = useState<any>(null)
  const [workflowUploadState, setWorkflowUploadState] = useState<UploadState>({
    active: false,
    percentage: 0,
    message: "",
  })

  const getWorkflowAction = (site: SiteData) => {
    const status = site.survey_workflow_status
    if (status === "site_created" && auth.hasPermission("Site", "mis") && site.status !== "cancelled" && !site.assigned_engineer_id) {
      return {
        type: "assign_engineer",
        label: "Assign Engineer",
        icon: <FiUserPlus size={14} />,
        color: "bg-blue-800 hover:bg-blue-900",
        fields: [
          {
            name: "user",
            label: "Site Engineer",
            type: "selectWithSearch",
            required: true,
            apiEndpoint: "/survey/sites/assignment/users",
            requestParams: { role_id: SITE_ENGINEER_ASSIGNABLE_ROLE_IDS, project_id: site.project_id, src: 'all' },
            mapOption: (u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name} (${u.username})` }),
          },
        ],
      }
    }
    if (status === "engineer_assigned" && auth.hasPermission("Site", "site_engineer")) {
      return {
        type: "start_field_work",
        label: "Start Field Work",
        icon: <FiActivity size={14} />,
        color: "bg-green-600 hover:bg-green-700",
        fields: [
          { name: "current_latitude", label: "Current Latitude", type: "text", required: true },
          { name: "current_longitude", label: "Current Longitude", type: "text", required: true },
          { name: "remarks", label: "Remarks", type: "textarea", required: false },
        ],
      }
    }
    if (status === "field_work_in_progress" && auth.hasPermission("Site", "site_engineer")) {
      return {
        type: "upload_data",
        label: "Upload Site Data",
        icon: <FiUpload size={14} />,
        color: "bg-purple-600 hover:bg-purple-700",
        fields: [
          { name: "files", label: "Files", type: "file", required: true, enablePhotoCategory: true },
          { name: "description", label: "Description", type: "textarea", required: false },
        ],
      }
    }
    if (status === "data_uploaded" && auth.hasPermission("Site", "site_engineer")) {
      return {
        type: "complete_field_work",
        label: "Complete Field Work",
        icon: <FiCheckCircle size={14} />,
        color: "bg-green-600 hover:bg-green-700",
        fields: [{ name: "remarks", label: "Remarks", type: "textarea", required: false }],
      }
    }
    if (status === "field_work_completed" && auth.hasPermission("Site", "technical_analyst")) {
      return {
        type: "ta_review",
        label: site.ta_userid ? "Update Technical Associate Review" : "Review Data",
        icon: <FiCheck size={14} />,
        color: "bg-green-600 hover:bg-green-700",
        fields: [
          { name: "action", label: "Action", type: "select", required: true, options: [{ value: "approve", label: "Approve" }, { value: "reject", label: "Reject" }] },
          { name: "remarks", label: "Remarks", type: "textarea", required: true },
          { name: "quality_feedback", label: "Quality Feedback", type: "textarea", required: false },
          { name: "rating", label: "Engineer Rating", type: "rating", required: false },
          { name: "files", label: "Additional Files", type: "file", required: false },
        ],
      }
    }
    if (status === "ta_review_completed" && auth.hasPermission("Site", "mis") && !site.assigned_report_maker_id) {
      return {
        type: "assign_report_maker",
        label: site.report_designer_userid ? "Update Report Maker" : "Assign Report Maker",
        icon: site.report_designer_userid ? <FiRefreshCw size={14} /> : <FiClipboard size={14} />,
        color: "bg-indigo-600 hover:bg-indigo-700",
        fields: [
          {
            name: "user",
            label: "Report Maker",
            type: "selectWithSearch",
            required: true,
            apiEndpoint: "/survey/sites/assignment/users",
            requestParams: { role_id: [9], project_id: site.project_id, src: 'all' },
            mapOption: (u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name} (${u.username})` }),
          },
          { name: "priority", label: "Priority", type: "priority", required: false },
        ],
      }
    }
    if (status === "report_maker_assigned" && auth.hasPermission("Site", "report_maker")) {
      return {
        type: "upload_report",
        label: "Upload Report / Reject",
        icon: <FiUpload size={14} />,
        color: "bg-green-600 hover:bg-green-700",
        fields: [
          { name: "action", label: "Action", type: "select", required: true, options: [{ value: "upload", label: "Upload Report" }, { value: "reject", label: "Reject to Technical Associate" }] },
          { name: "files", label: "Report Files", type: "file", required: false, accept: "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.dwg", shouldShow: (fd: any) => fd.action === "upload" },
          { name: "remarks", label: "Remarks", type: "textarea", required: false },
          { name: "rejection_reason", label: "Rejection Reason", type: "textarea", required: true, shouldShow: (fd: any) => fd.action === "reject" },
          { name: "quality_feedback", label: "Quality Feedback", type: "textarea", required: false },
        ],
      }
    }
    if (status === "report_completed" && auth.hasPermission("Site", "mis") && !site.assigned_validator_id) {
      return {
        type: "assign_validator",
        label: site.validator_userid ? "Update Validator" : "Assign Validator",
        icon: site.validator_userid ? <FiRefreshCw size={14} /> : <FiShield size={14} />,
        color: "bg-blue-800 hover:bg-blue-900",
        fields: [
          {
            name: "user",
            label: "Validator",
            type: "selectWithSearch",
            required: true,
            apiEndpoint: "/survey/sites/assignment/users",
            requestParams: { role_id: "10,5,4,6,3", project_id: site.project_id, src: 'all' },
            mapOption: (u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name} (${u.username})` }),
          },
          { name: "priority", label: "Priority", type: "priority", required: false },
        ],
      }
    }
    if (status === "validator_assigned" && auth.hasPermission("Site", "validator")) {
      return {
        type: "validate_report",
        label: "Validate Report",
        icon: <FiCheckCircle size={14} />,
        color: "bg-green-600 hover:bg-green-700",
        fields: [
          { name: "action", label: "Action", type: "select", required: true, options: [{ value: "approve", label: "Approve" }, { value: "reject", label: "Reject" }] },
          { name: "remarks", label: "Remarks", type: "textarea", required: true },
          { name: "qa_feedback", label: "QA Feedback", type: "textarea", required: false },
          { name: "rating", label: "Technical Associate Rating", type: "rating", required: false },
          { name: "files", label: "Upload Files", type: "file", required: false },
        ],
      }
    }
    if (status === "report_finalized" && auth.hasPermission("Site", "mis")) {
      return {
        type: "send_to_client",
        label: "Send to Client",
        icon: <FiSend size={14} />,
        color: "bg-blue-800 hover:bg-blue-900",
        fields: [{ name: "remarks", label: "Remarks", type: "textarea", required: false }],
      }
    }
    return null
  }

  const handleWorkflowAction = async (actionType: string, data: any) => {
    if (!siteData) return
    try {
      const fileUploadActionMap: Record<string, string> = {
        upload_data: "site-data",
        ta_review: "project_sites/ta-review",
        upload_report: "project_sites/reports",
        validate_report: "project_sites/validator-files",
      }
      const workflowFiles = Array.isArray(data.files) ? (data.files as WorkflowUploadFile[]) : []
      const hasFiles = workflowFiles.length > 0
      let reqBody: any = {}
      Object.keys(data).forEach((key) => {
        if (key !== "files" && data[key] !== undefined && data[key] !== null && data[key] !== '') reqBody[key] = data[key]
      })
      const endpointMap: Record<string, string> = {
        assign_engineer: `/survey/sites/${siteData.id}/assign-engineer`,
        start_field_work: `/survey/sites/${siteData.id}/start-field-work`,
        upload_data: `/survey/sites/${siteData.id}/upload-data`,
        complete_field_work: `/survey/sites/${siteData.id}/complete-field-work`,
        ta_review: `/survey/sites/${siteData.id}/ta-review`,
        assign_report_maker: `/survey/sites/${siteData.id}/assign-report-maker`,
        upload_report: `/survey/sites/${siteData.id}/upload-report`,
        assign_validator: `/survey/sites/${siteData.id}/assign-validator`,
        validate_report: `/survey/sites/${siteData.id}/validate-report`,
        send_to_client: `/survey/sites/${siteData.id}/send-to-client`,
      }
      const endpoint = endpointMap[actionType] || ""
      if (hasFiles && fileUploadActionMap[actionType]) {
        const sharedDescription = typeof data.description === "string" && data.description.trim() ? data.description.trim() : undefined
        setWorkflowUploadState({ active: true, percentage: 0, message: "Uploading files..." })
        const uploadedFiles = await uploadFilesWithPresignedPayload(
          workflowFiles.map(({ file, photoCategory }) => ({
            file,
            ...(sharedDescription ? { description: sharedDescription } : {}),
            ...(photoCategory ? { photoCategory } : {}),
          })),
          {
            siteId: siteData.id,
            folderName: fileUploadActionMap[actionType],
            onProgress: (progress) => {
              setWorkflowUploadState({
                active: true,
                percentage: progress.percentage,
                message: progress.activeFileName
                  ? `Uploading ${progress.activeFileName} (${progress.completedFiles}/${progress.totalFiles})`
                  : "Uploading files...",
              })
            },
          }
        )
        await axiosInstance.post(endpoint, { ...reqBody, uploadedFiles })
      } else {
        await axiosInstance.post(endpoint, reqBody)
      }
      setWorkflowActionModalOpen(false)
      setWorkflowCurrentAction(null)
      fetchSiteData()
      toast.success("Workflow action completed successfully")
    } catch (error) {
      console.error(`Error performing ${actionType}:`, error)
      toast.error("Failed to complete workflow action")
    } finally {
      setWorkflowUploadState({ active: false, percentage: 0, message: "" })
    }
  }

  const canViewWorkflow = auth.hasPermission('Site', 'workflow');
  const canViewOverview = auth.hasPermission('Site', 'overview');
  const canViewMembers = auth.hasPermission('Site', 'site_members');
  const canViewHistory = auth.hasPermission('Site', 'history');
  const canViewBilling = auth.hasPermission('Site', 'billing');
  const canViewRequirements = auth.hasPermission('Site', 'req_view_project') ||
                               auth.hasPermission('Site', 'req_view_activity') ||
                               auth.hasPermission('Site', 'req_view_common');
  const tabs = [
    ...(canViewWorkflow ? [{ id: 'workflow', label: 'Workflow', icon: FiActivity }] : []),
    ...(canViewOverview ? [{ id: 'overview', label: 'Overview', icon: FiInfo }] : []),
    ...(canViewMembers ? [{ id: 'members', label: 'Site Members', icon: FiUserPlus }] : []),
    ...(canViewRequirements ? [{ id: 'requirements', label: 'Requirements', icon: FiFileText }] : []),
    ...(canViewHistory ? [{ id: 'history', label: 'History', icon: FiClock }] : []),
    ...(canViewBilling ? [{ id: 'billing', label: 'Billing', icon: FiHash }] : []),
  ];

  // Set default active tab to first available when permissions resolve
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id)
    }
  }, [canViewWorkflow, canViewOverview, canViewMembers, canViewHistory, canViewBilling, canViewRequirements])

  const fetchSiteData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/survey/sites/${id}`);
      setSiteData(response.data.data.site);
    } catch (error) {
      console.error('Error fetching site data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlideshow = async () => {
    if (!id) return
    try {
      const res = await axiosInstance.get(`/slideshow/sites/${id}`)
      if (res.data.success && res.data.data.slideshow) {
        setSlideshowData(res.data.data.slideshow)
      } else {
        setSlideshowData(null)
      }
    } catch {
      setSlideshowData(null)
    }
  }

  const handleCreateOrRegenerateSlideshow = async () => {
    if (!id) return
    setSlideshowLoading(true)
    try {
      await axiosInstance.post(`/slideshow/sites/${id}`, { expiry_days: slideshowForm.expiry_days })
      await fetchSlideshow()
    } catch {
      // toast shown by axios interceptor
    } finally {
      setSlideshowLoading(false)
    }
  }

  const handleExtendExpiry = async (days: number) => {
    if (!id) return
    setSlideshowLoading(true)
    try {
      await axiosInstance.patch(`/slideshow/sites/${id}/extend`, { expiry_days: days })
      await fetchSlideshow()
    } catch {
      // toast shown by axios interceptor
    } finally {
      setSlideshowLoading(false)
    }
  }

  const getSlideshowPublicUrl = () => {
    if (!slideshowData?.slug) return ""
    const origin = window.location.origin
    const siteId = siteData?.site_id || ''
    const fwDate = siteData?.ta_work_date || ''
    return `${origin}/slideshow/${slideshowData.slug}?site_id=${siteId}&fw_date=${fwDate}`
  }

  useEffect(() => {
    if (id) {
      fetchSiteData();
      fetchSlideshow();
    }
  }, [id]);

  const openModal = (action: 'hold' | 'cancel' | 'resume') => {
    setModalState({ isOpen: true, action });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, action: null });
  };

  const handleActionSuccess = () => {
    fetchSiteData();
  };

  const renderTabContent = () => {
    if (!siteData) return null;

    switch (activeTab) {
      case 'overview':
        return <OverviewTab siteData={siteData} />;
      case 'workflow':
        return <WorkflowTab siteData={siteData} onRefresh={fetchSiteData} />;
      case 'members':
        return <SiteMembersTab siteData={siteData} />;
      case 'requirements':
        return <RequirementsTab siteData={siteData} />;
      case 'history':
        return <HistoryTab siteId={siteData.id} />;
      case 'billing':
        return <BillingTab siteData={siteData} />;
      case 'claim':
        return <ClaimTab siteId={siteData.id} />;
      default:
        return <OverviewTab siteData={siteData} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading site details...</p>
        </div>
      </div>
    );
  }

  if (!siteData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Site Not Found</h2>
          <p className="text-gray-600">
            The site you're looking for doesn't exist or you don't have permission to view it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Site Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center justify-between w-full">
              <div className="w-12 mr-2 h-12 bg-gradient-to-br from-blue-800 to-blue-500 rounded flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {siteData.site_id.substring(0, 2).toUpperCase()}
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">{siteData.site_id}</h1>
                <p className="text-sm text-gray-600 mb-1">{siteData.site_name || 'N/A'}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <div className="flex items-center gap-1 min-w-0 max-w-[220px] sm:max-w-[320px] lg:max-w-[520px]">
                    <FiMapPin size={12} />
                    <span className="block truncate">{siteData.site_address || 'No address'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FiCalendar size={12} />
                    Created {formatDate(siteData.created_at)}
                  </div>
                  <div className="flex items-center gap-1">
                    <FiCalendar size={12} />
                    Updated {formatDate(siteData.updated_at)}
                  </div>
                </div>
              </div> <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={fetchSiteData}
                  className="flex items-center gap-2 px-3 py-2 text-blue-800 bg-blue-50 rounded hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  <FiRefreshCw size={14} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                {canViewSlideshow && (
                  <button
                    onClick={() => setShowSlideshowModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-purple-700 bg-purple-50 rounded hover:bg-purple-100 transition-colors text-sm font-medium"
                    title="Manage Slideshow"
                  >
                    <FiPlay size={14} />
                    <span className="hidden sm:inline">Slideshow</span>
                  </button>
                )}
                {canViewFieldData && (
                  <button
                    onClick={() => openStepDetails('data_uploaded')}
                    className="flex items-center gap-2 px-3 py-2 text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors text-sm font-medium"
                    title="View Field Data WIP"
                  >
                    <FiUpload size={14} />
                    <span className="hidden sm:inline">Field Data</span>
                  </button>
                )}
                {canViewUploadedData && (
                  <button
                    onClick={() => setShowUploadedData(true)}
                    className="flex items-center gap-2 px-3 py-2 text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100 transition-colors text-sm font-medium"
                    title="View Uploaded Data Files"
                  >
                    <FiFolder size={14} />
                    <span className="hidden sm:inline">Uploaded Data</span>
                  </button>
                )}
                {canDownloadAllFiles && (
                  <button
                    onClick={handleDownloadAllFiles}
                    disabled={downloadingAll}
                    className="flex items-center gap-2 px-3 py-2 text-teal-700 bg-teal-50 rounded hover:bg-teal-100 transition-colors text-sm font-medium disabled:opacity-60"
                    title="Download every file of this site as one ZIP"
                  >
                    <FiDownload size={14} />
                    <span className="hidden sm:inline">{downloadingAll ? 'Zipping...' : 'Download All Files'}</span>
                  </button>
                )}

                {(() => {
                  const workflowAction = getWorkflowAction(siteData)
                  return workflowAction ? (
                    <button
                      onClick={() => { setWorkflowCurrentAction(workflowAction); setWorkflowActionModalOpen(true) }}
                      className={`flex items-center gap-2 px-3 py-2 text-white rounded transition-colors text-sm font-medium ${workflowAction.color}`}
                    >
                      {workflowAction.icon}
                      <span className="hidden sm:inline">{workflowAction.label}</span>
                    </button>
                  ) : null
                })()}

                {siteData.status === 'hold' || siteData.status === 'cancelled' || (siteData.survey_workflow_status !== 'site_created' && siteData.survey_workflow_status !== 'engineer_assigned') ? (
                  <>
                    {siteData.status === "cancelled" && (
                      <StatusBadge status={siteData.status} />
                    )}

                    {siteData.status === "hold" && (
                      <>
                        <StatusBadge status={siteData.status} />
                        <button
                          onClick={() => openModal('resume')}
                          className="flex items-center gap-2 px-3 py-2 text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors text-sm font-medium"
                        >
                          <FiPlay size={14} />
                          <span>Resume Site</span>
                        </button>
                      </>
                    )}

                  </>
                ) : (
                  <>
                    <button
                      onClick={() => openModal('hold')}
                      className="flex items-center gap-2 px-3 py-2 text-yellow-700 bg-yellow-50 rounded hover:bg-yellow-100 transition-colors text-sm font-medium"
                    >
                      <FiPause size={14} />
                      <span>Hold Site</span>
                    </button>

                    <button
                      onClick={() => openModal('cancel')}
                      className="flex items-center gap-2 px-3 py-2 text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors text-sm font-medium"
                    >
                      <FiXCircle size={14} />
                      <span>Cancel Site</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded shadow-sm border border-gray-200  mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                      ? 'border-blue-800 text-blue-800'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>

      {/* Hold/Cancel Modal */}
      <CancelHoldModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        action={modalState.action}
        siteId={id!}
        onSuccess={handleActionSuccess}
      />

      {/* Workflow Action Modal */}
      {workflowCurrentAction && (
        <ActionModal
          isOpen={workflowActionModalOpen}
          onClose={() => setWorkflowActionModalOpen(false)}
          title={workflowCurrentAction.label}
          onSubmit={(data) => handleWorkflowAction(workflowCurrentAction.type, data)}
          uploadState={workflowUploadState}
          fields={workflowCurrentAction.fields}
        />
      )}

      {/* Uploaded Data Offcanvas */}
      {showUploadedData && siteData && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowUploadedData(false)} />
          <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-semibold text-gray-800">Uploaded Data Files</h3>
              <button onClick={() => setShowUploadedData(false)} className="p-1.5 hover:bg-gray-100 rounded">
                <FiX size={16} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <SiteFilesSection siteId={siteData.id} />
            </div>
          </div>
        </>
      )}

      {/* Slideshow Modal */}
      {showSlideshowModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => !slideshowLoading && setShowSlideshowModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Site Slideshow</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Generate a public URL for this site's media</p>
                </div>
                <button
                  onClick={() => setShowSlideshowModal(false)}
                  className="p-2 rounded hover:bg-gray-100 text-gray-500"
                  disabled={slideshowLoading}
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {slideshowData && (
                  <div className={`p-3 rounded-lg border text-sm ${slideshowData.is_expired ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium ${slideshowData.is_expired ? 'text-red-700' : 'text-green-700'}`}>
                        {slideshowData.is_expired ? 'Expired Slideshow' : 'Active Slideshow'}
                      </span>
                      {!slideshowData.is_expired && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const url = getSlideshowPublicUrl()
                              navigator.clipboard.writeText(url).then(() => toast.success("URL copied!"))
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Copy URL
                          </button>
                          <button
                            onClick={() => window.open(getSlideshowPublicUrl(), '_blank', 'noopener,noreferrer')}
                            className="text-xs text-green-600 hover:underline"
                          >
                            Open URL
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div>
                        <span className="font-medium">Expires: </span>
                        {slideshowData.expiry_at ? new Date(slideshowData.expiry_at).toLocaleString() : "—"}
                      </div>
                      {slideshowData.generated_by && (
                        <div>
                          <span className="font-medium">Generated by: </span>
                          {formatSlideshowGeneratedBy(slideshowData.generated_by)}
                        </div>
                      )}
                      {(slideshowData.last_regenerated_at || slideshowData.created_at) && (
                        <div>
                          <span className="font-medium">Last regenerated: </span>
                          {new Date(slideshowData.last_regenerated_at || slideshowData.created_at || "").toLocaleString()}
                        </div>
                      )}
                    </div>
                    {!slideshowData.is_expired && (
                      <div className="mt-2 flex gap-2">
                        {[7, 14, 30].map((days) => (
                          <button
                            key={days}
                            onClick={() => handleExtendExpiry(days)}
                            disabled={slideshowLoading}
                            className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                          >
                            +{days}d
                          </button>
                        ))}
                        <span className="text-xs text-gray-400 self-center">extend expiry</span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {slideshowData ? "Regenerate with new expiry (days)" : "Expiry (days from now)"}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={slideshowForm.expiry_days}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val)) setSlideshowForm({ expiry_days: Math.min(30, Math.max(1, val)) });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">1–30 days. Previous URL will be invalidated on regeneration.</p>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    onClick={() => setShowSlideshowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm"
                    disabled={slideshowLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateOrRegenerateSlideshow}
                    disabled={slideshowLoading}
                    className="px-4 py-2 rounded text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 font-medium text-sm"
                  >
                    {slideshowLoading ? "Processing..." : slideshowData ? "Regenerate URL" : "Generate URL"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {siteData && (
        <StepDetailsOffcanvas
          isOpen={headerStepDetailsOpen}
          onClose={() => setHeaderStepDetailsOpen(false)}
          siteId={siteData.id}
          status={headerSelectedStep}
        />
      )}
    </div>
  );
};

export default SiteDetails
